import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { OptionsObject, SnackbarMessage } from 'notistack';

import apiEndpoints from '@src/lib/apiEndpoints';
import { clearUserData } from '@src/redux/action/login';
import { store } from '@src/redux/store';

// You can set your base URL here
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

let enqueueSnackbar: ((message: SnackbarMessage, options?: OptionsObject | undefined) => React.ReactText) | null = null;

export function setSnackbar(
  enqueueSnackbarFn: (message: SnackbarMessage, options?: OptionsObject) => React.ReactText
): void {
  enqueueSnackbar = enqueueSnackbarFn;
}

let isRefreshing = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  timeout: 10000,
  timeoutErrorMessage: 'Request timed out',
});

axiosInstance.interceptors.response.use(
  async (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry: boolean };
    const dispatch = store.dispatch;

    if (
      error?.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== apiEndpoints.AUTH_REFRESH_TOKEN
    ) {
      if (!isRefreshing) {
        originalRequest._retry = true;
        isRefreshing = true;
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          return (window.location.href = '/login');
        }

        try {
          const refreshResponse = await axiosInstance.post(apiEndpoints.AUTH_REFRESH_TOKEN, { refreshToken });

          const {
            access: { token },
            refresh: { token: newRefreshToken },
          } = refreshResponse.data;

          localStorage.setItem('accessToken', token);
          localStorage.setItem('refreshToken', newRefreshToken);
          axiosInstance.defaults.headers['Authorization'] = `Bearer ${token}`;
          originalRequest.headers['Authorization'] = `Bearer ${token}`;

          processQueue(null, token);

          return axiosInstance(originalRequest);
        } catch (err) {
          processQueue(err as AxiosError, null);
          localStorage.clear();
          dispatch(clearUserData());
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        })
        .catch(err => {
          return Promise.reject(err);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorMessage = (error as any)?.response?.data?.message || 'An unknown error occurred';

    if (enqueueSnackbar && error?.response?.status !== 401) {
      enqueueSnackbar(`An error occurred: ${errorMessage}`, {
        variant: 'error',
        autoHideDuration: 2000,
      });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
